import moment from 'moment';
import { localStorageGet } from '../../utils/storage';
import type { SelectListUser } from '../CreateMessage/createMessageSlice/types';
import type { GroupMember } from '../GroupDetail/groupDetailSlice/types';
import type { Group } from '../GroupsList/groupsSlice/types';
import { mapGroupMemberToSelectUser } from '../MemberSettings/helpers';
import type {
  LoneWorkerPersonalAlarmCreateBody,
  LoneWorkerPersonalAlarmResponse,
  Time,
} from './loneWorkerTimerSlice/types';

export type LwtPreperationData = {
  selectedGroups?: Group[];
  selectedUsers: SelectListUser[];
  timeSelected?: Time;
  messageText: string;
  hasPreAlarmNotifications?: boolean;
};

export const calculateSecondsBetweenTwoDateTimes = (earliestDate: Date, latestDate: Date) => {
  return (+latestDate - +earliestDate) / 1000;
};

export const secondsToTimespan = (totalSeconds: number) => {
  return moment.utc(totalSeconds * 1000).format('HH:mm:ss');
};

/** @note Can return negative values */
export const timespanToSeconds = (timespan: string) => {
  return moment.duration(timespan).asSeconds();
};

export const calcLwtProgress = (lwt: LoneWorkerPersonalAlarmResponse, displaySeconds: number) => {
  const duration = moment.utc(lwt.DispatchTime).diff(moment.utc(lwt.CreatedTime)) / 1000;
  const seconds = displaySeconds;

  if (!duration) return 0;

  if (seconds <= 0) return 1;
  if (seconds > duration) return 0;

  const progress = 1 - seconds / duration;
  return progress;
};

export const prepareLwtData = (data: LwtPreperationData) => {
  const {
    timeSelected,
    selectedGroups = [],
    messageText,
    hasPreAlarmNotifications,
    selectedUsers,
  } = data;
  const secondsToAdd = timeSelected ? (timeSelected?.hours * 60 + timeSelected?.minutes) * 60 : 0;
  const alarmData: LoneWorkerPersonalAlarmCreateBody = {
    groupIDs: selectedGroups.map((group: Group) => group.id),
    deviceId: localStorageGet('pushyToken'),
    text: messageText,
    oneMinuteWarning: hasPreAlarmNotifications === undefined ? true : hasPreAlarmNotifications,
    locationId: null,
    dispatchDelay: secondsToTimespan(secondsToAdd),
  };
  alarmData.recipientIds = selectedUsers.map(user => user.id);

  return alarmData;
};

export const getRecipientsText = (
  selectedGroups: Group[],
  selectedUsers: SelectListUser[],
  authUserId: number
) => {
  let text = '';
  if (selectedGroups.length || selectedUsers.length) {
    const groupsNames = selectedGroups.map(group => group.name);
    const usersNames = selectedUsers
      .filter(user => !user.groupIds?.length && authUserId !== user.id)
      .map(user => user.displayName);
    text = [...groupsNames, ...usersNames].join(', ');
    if (text?.length) {
      text = text.replace(/,\s*$/, '');
    }
  }
  return text;
};

export const getIfCanActivateLwt = (data: LwtPreperationData, authUserId: number) => {
  const { timeSelected, messageText, selectedUsers } = data;
  return Boolean(
    timeSelected &&
      (timeSelected.hours || timeSelected.minutes) &&
      messageText.length &&
      selectedUsers.filter(user => authUserId !== user.id).length
  );
};

export const addGroupMembersToSelectedAndGetList = (
  members: GroupMember[],
  selectedUsersList: SelectListUser[]
) => {
  const selectedUsers = [...selectedUsersList];
  members.forEach(member => {
    const userIndex = selectedUsers.findIndex(user => user.id === member.userID);
    if (!member.groupID) {
      return;
    }
    if (userIndex > -1) {
      const newUser = { ...selectedUsers[userIndex] };
      newUser.groupIds = newUser.groupIds
        ? [...newUser.groupIds, member.groupID]
        : [member.groupID];
      selectedUsers[userIndex] = newUser;
    } else {
      const selectedUser: SelectListUser = mapGroupMemberToSelectUser(member, {
        isSelected: true,
        groupIds: [member.groupID],
      });
      selectedUsers.push(selectedUser);
    }
  });
  return selectedUsers;
};

export const getSelectedUsersAfterGroupUnselection = (
  selectedUsersList: SelectListUser[],
  groupId: number
) => {
  const usersInSelectedGroup = selectedUsersList.filter(user => user.groupIds?.includes(groupId));
  const usersNotInSelectedGroup = selectedUsersList.filter(
    user => !user.groupIds?.includes(groupId)
  );
  const newSelectedUsers = [...usersNotInSelectedGroup];
  usersInSelectedGroup.forEach(user => {
    const newUser = { ...user };
    newUser.groupIds = user.groupIds?.filter(id => id !== groupId);
    if (newUser.groupIds?.length) {
      newSelectedUsers.push(newUser);
    }
  });
  return newSelectedUsers;
};

export const getActiveGroupsAfterUserUnselection = (
  selectedUsers: SelectListUser[],
  selectedGroups: Group[],
  authUserId: number
) => {
  let activeGroupsIds: number[] = [];
  selectedUsers.forEach(user => {
    if (user.groupIds && user.id !== authUserId) {
      activeGroupsIds = [...activeGroupsIds, ...user.groupIds];
    }
  });
  const uniqueActiveGroupsIds = Array.from(new Set(activeGroupsIds));
  return selectedGroups.filter(group => uniqueActiveGroupsIds.includes(group.id));
};
